import { useCallback } from 'react'

import { AppStatus } from '@/redux'
import { analytics } from '@/services/analytics/segment'
import { rdt } from '@/services/analytics/reddit'
import { QueryKeys } from '@/services/api/session/queryKeys'
import { useCreateProfile } from '@/services/api/session/useProfile'
import type { Profile } from '@/services/api/session/views'

import { ProviderId } from '@/features/Auth/providers/types'
import { signIn } from '@/features/Auth/session/signIn'
import { authStore } from '@/features/Auth/store/authStore'
import { warnEmailInUse } from '@/features/Auth/session/utils'
import { ERRORS, isEmailAvailable } from '@/features/Auth/session/views'

/**
 * Creating the profile this server is missing for an identity.
 *
 * The credential half is gone from here: obtaining one — creating the Firebase account, or
 * recovering the one an abandoned signup left behind — belongs to the e-mail provider, and
 * every other source arrives already holding a session. What is left is the part that was
 * always this hook's job, which is the profile.
 *
 * The machine is told when the profile exists, so a signup and a social sign-in end in the
 * same state by the same event. See EPIC-005.
 */
export function useSignup() {
  const createProfile = useCreateProfile()

  const signup = useCallback(
    async (data: Partial<Profile>, source: ProviderId = 'email') => {
      try {
        AppStatus.set('loading')

        const emailAvailable = await isEmailAvailable(data?.email)

        if (!emailAvailable) {
          // Whatever the provider, a profile already holding this address on this server is
          // not the person signing up: the identity being registered has no profile here.
          warnEmailInUse()
          throw ERRORS.emailInUse
        }

        if (source === 'email') {
          await signIn('email', { email: data?.email, password: data?.password }, authStore.dispatch)

          if (authStore.get().status === 'failed') {
            warnEmailInUse()
            throw ERRORS.emailInUse
          }
        }

        const user = await createProfile.mutateAsync(data)

        QueryKeys.me.setData([], user)
        authStore.dispatch({ type: 'profileCreated', uid: user?.id })

        analytics.track('sign_up', {
          sign_up_source: source,
          email: data?.email,
          verification_link: user?.email_verification_link
        })
        rdt('SignUp')
        analytics.track('login', { email: data?.email })

        return { user }
      } catch (e) {
        AppStatus.set('idle')
        logger.error('Error on signup', e, 'Auth')

        return { user: null }
      }
    },
    [createProfile]
  )

  return { signup }
}
